import React, { useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Link, Stack, Typography } from '@mui/material';

import { AppContext } from '../../../../context';
import { GroupsContext } from '../context';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import useGroup from '../_data_sources/useGroup';
import useGroupRoles from '../_data_sources/useGroupRoles';
import useGroupUsers from '../_data_sources/useGroupUsers';
import useGroupManuals from '../_data_sources/useGroupManuals';
import Overview from './Overview';
import Users from './Users';
import useUrlParams from 'hooks/useUrlParams';
import Roles from './Roles';
import Manuals from './Manuals';

type GroupTab = {
  label: string;
  key: string;
  component: React.ReactNode;
  count?: number;
};

type GroupUrlParams = {
  tab: string;
};

const defaultParams: GroupUrlParams = {
  tab: 'overview',
};

export default function GroupDetails() {
  const { currentOrganizationId, hasAppPermission, hasPermission } = useContext(AppContext);
  const { deleteGroup } = useContext(GroupsContext);

  const { id } = useParams();
  const navigate = useNavigate();
  const groupQuery = useGroup(currentOrganizationId, id);
  const [params, setParams] = useUrlParams(defaultParams);
  const groupRolesQuery = useGroupRoles(
    currentOrganizationId || '',
    id || '',
    {},
    { enabled: !!hasPermission('listRolesForGroup', currentOrganizationId) }
  );
  const groupUsersQuery = useGroupUsers(
    currentOrganizationId || '',
    id || '',
    {},
    { enabled: !!hasPermission('listUsersInGroup', currentOrganizationId) }
  );
  const groupManualsQuery = useGroupManuals(
    currentOrganizationId || '',
    id || '',
    {},
    { enabled: !!hasPermission('listManualPermissions', currentOrganizationId) }
  );

  const tabs: { [key: string]: GroupTab } = {
    overview: {
      label: t('groups.details.tabs.details'),
      key: 'details',
      component: <Overview />,
    },
  };
  if (
    currentOrganizationId &&
    hasPermission('listUsersInGroup', currentOrganizationId) &&
    hasPermission('listUsers', currentOrganizationId)
  ) {
    tabs.users = {
      label: t('groups.details.tabs.users'),
      key: 'users',
      component: <Users />,
      count: groupUsersQuery.data?.pages[0]?.count,
    };
  }

  if (
    currentOrganizationId &&
    hasPermission('listRolesForGroup', currentOrganizationId) &&
    hasPermission('listRoles', currentOrganizationId)
  ) {
    tabs.roles = {
      label: t('groups.details.tabs.roles'),
      key: 'roles',
      component: <Roles />,
      count: groupRolesQuery.data?.roles?.length,
    };
  }

  if (
    currentOrganizationId &&
    hasPermission('listManualPermissions', currentOrganizationId) &&
    hasAppPermission('listIssuedManuals')
  ) {
    tabs.manuals = {
      label: t('groups.details.tabs.manuals'),
      key: 'manuals',
      component: <Manuals roleId={groupQuery.data?.childRoleId ?? ''} />,
      count: (groupManualsQuery.data?.manuals ?? []).filter((m) => !!m.persona).length,
    };
  }

  const updateParams = React.useCallback(
    (updatedParams: GroupUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const breadcrumbItems = [
    <Link
      key='b1'
      href='..'
    >
      {t('groups.title')}
    </Link>,
    <Typography key='b4'>{groupQuery.data?.name || ''}</Typography>,
  ];

  const actions = (
    <Stack
      direction='row'
      spacing={2}
    >
      {currentOrganizationId && hasPermission('updateGroup', currentOrganizationId) && (
        <Button
          variant='contained'
          size='small'
          href={`../edit/${groupQuery.data?.id}`}
        >
          {t('groups.actions.edit')}
        </Button>
      )}
      {currentOrganizationId && hasPermission('deleteGroup', currentOrganizationId) && (
        <Button
          color='error'
          variant='contained'
          size='small'
          onClick={() => deleteGroup(id!, () => navigate('..'))}
        >
          {t('groups.actions.delete')}
        </Button>
      )}
    </Stack>
  );

  return (
    <CommonLayout
      headerProps={{
        title: groupQuery.data?.name || '',
        breadcrumbItems,
        actions,
      }}
      tabs={{
        items: Object.entries(tabs).map(([key, tab]) => ({
          label: tab.label,
          value: key,
          count: tab.count,
        })),
        value: params.tab,
        onChange: (event: React.SyntheticEvent, newValue: string | number) =>
          updateParams({ tab: newValue.toString() }),
      }}
    >
      {tabs[params.tab]?.component}
    </CommonLayout>
  );
}
